import { Box, Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { FastField } from 'formik';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { filteredStipType, getOptions, newGridRecordId } from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { IPhoenixGridActions, PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { TextInputField } from '@shared/fields/TextInputField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { TextInput } from '@shared/inputs/TextInput';
import { AddButton, PageContent, ReasonsGridWrapper } from '@styles/Admin-tools/Workflows/WorkflowStepStyles';
import { CommonButtonWrapper } from '@styles/CommonStyles';

import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { useStipTypes } from '@shared/hooks/useStipTypes';
import { IRole } from '../../../QueueConfiguration/types';
import { colorsMock, orderingMock, reasonLenderMappingValidationSchema, stepTypesOptionsMock } from '../../constants';
import { requestCreateReason, requestDeleteReason, requestGetReasonsByWorkflowStep, requestUpdateReason } from '../../requests';
import {
  ICreateReasonRequest,
  ILender,
  ILenderData,
  IReason,
  IStepFormProps,
  IWorkflowStep,
  StepTypeEnum,
  TStep,
  TTRKRData,
} from '../../types';
import { StepList } from '../StepList';

export const StepForm = ({
  setFieldValue,
  handleClose,
  values,
  isEditMode,
  stepData,
  refreshWorflow,
  workflows,
  workflowLevel,
}: IStepFormProps<IWorkflowStep>) => {
  const { data: trkrStages = [] } = useSWR<TTRKRData[]>(`/${SERVICES.WORKFLOW}/trkr/stages`, {revalidateOnMount: undefined});
  const { data: trkrSteps = [] } = useSWR<TTRKRData[]>(`/${SERVICES.WORKFLOW}/trkr/steps`);
  const { data: roles = [] } = useSWR<IRole[]>(`/${SERVICES.USER}/roles`);

  const { data: lenders = [] } = useSWR<ILender[]>(`/${SERVICES.LENDER}/lenders`);

  const [stepType, setStepType] = useState<TStep>(values?.stepType || 'Step');
  const [reasonsData, setReasonsData] = useState<IReason[]>(stepData?.reasons ?? []);
  const [loading, setLoading] = useState(false);

  const { stipTypes } = useStipTypes();

  const stipTypeOptions = useMemo(() => getOptions(filteredStipType(stipTypes)), [stipTypes]);
  const sortedLenders = useMemo(() => lenders.sort((a: ILender, b: ILender) =>
    a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
  ) ?? [], [lenders]);

  const handleChangeDestStep = useCallback((step: IWorkflowStep | null) => {
    setFieldValue?.('reRouteWorkflowStepId', step?.id || null);
    setFieldValue?.('stepPath', step?.stepPath || '');
  }, []);

  const handleStepTypeChange = useCallback((value: TStep) => {
    setStepType(value);
    setFieldValue?.('stepType', value);
  }, []);

  const reasonsColumns: GridColDef[] = useMemo(() => {
    return [
      dataGridCol('label', {
        renderEditCell: (params) => <GridColTextInput params={params} />,
      }),
      dataGridCol('description', {
        renderEditCell: (params) => <GridColTextInput params={params} />,
      }),
      dataGridCol('lenderIds', {
        headerName: 'Lenders',
        renderEditCell: (params) => {
          const updatedParams = { ...params, value: params.row.lenderIds };
          return (
            <GridColSelect
              isSearchable
              multiple
              limitTags={1}
              options={getOptions(sortedLenders)}
              params={updatedParams}
            />
          );
        },

        renderCell: ({ row }) => (
          <div>
            {row.lenderList && (
              <ul style={{ paddingInlineStart: '16px' }}>
                {row.lenderList.map((lenderData: ILenderData) => (
                  <li key={lenderData.id}>{lenderData.lenderName}</li>
                ))}
              </ul>
            )}
          </div>
        ),
      }),
      dataGridCol('stipTypeId', {
        headerName: 'Stip Type',
        renderEditCell: (params) => (
          <GridColSelect
            options={stipTypeOptions}
            params={params}
          />
        ),
        valueFormatter: (params) => stipTypeOptions.find((stipType) => stipType.value == params.value)?.label ?? '',
      }),
    ].map((column) => ({ editable: true, flex: 1, ...column }));
  }, [sortedLenders, stipTypeOptions]);

  const reloadReasons = useCallback(async () => {
    if (stepData?.id) {
      const reasonsResponse = await requestGetReasonsByWorkflowStep(stepData?.id);
      setReasonsData(reasonsResponse);
    }
    setLoading(false);
  }, [stepData?.id]);

  const reasonGridActions: IPhoenixGridActions = {
    onSave: async (row: IReason) => {
      setLoading(true);
      const requestData: ICreateReasonRequest = {
        label: row.label,
        description: row.description,
        autoSelectLenders: row.lenderIds ?? [],
        stipTypeId: row.stipTypeId,
      };
      if (row.id === newGridRecordId) {
        const createReasonData: ICreateReasonRequest = {
          ...requestData,
          workflowStepId: stepData?.id,
          numericId: 0,
        };
        await requestCreateReason(createReasonData);
      } else {
        await requestUpdateReason(row.id, requestData);
      }
      reloadReasons();
      refreshWorflow();
    },
    onDelete: async (row: IReason) => {
      setLoading(true);
      await requestDeleteReason(row.id);
      reloadReasons();
      refreshWorflow();
    },
  };

  return (
    <>
      <PageContent>
        <Grid
          container
          spacing={2}
          mb={2}
        >
          {!isEditMode && (
            <Grid
              item
              xs={6}
            >
              <FastField
                hideNoneOption
                name="stepType"
                label="Please Select"
                value={values?.stepType ?? null}
                options={stepTypesOptionsMock}
                component={SelectAutocompleteField}
                onChange={handleStepTypeChange}
              />
            </Grid>
          )}

          <Grid
            item
            xs={6}
          >
            <FastField
              name="name"
              label="Step name"
              value={values?.name}
              component={TextInputField}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setFieldValue?.('name', event?.target?.value)}
            />
          </Grid>

          <Grid
            item
            xs={6}
          >
            <TextInput
              label="Description"
              value={values?.description}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setFieldValue?.('description', event?.target?.value)}
            />
          </Grid>

          <Grid
            item
            xs={6}
          >
            <TextInput
              label="Minutes of Green"
              value={values?.minutesGreen}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setFieldValue?.('minutesGreen', event?.target?.value)}
            />
          </Grid>

          <Grid
            item
            xs={6}
          >
            <TextInput
              label="Minutes of Red"
              value={values?.minutesRed}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setFieldValue?.('minutesRed', event?.target?.value)}
            />
          </Grid>
          <Grid
            item
            xs={6}
          >
            <Stack
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
            >
              <Box
                minWidth={130}
                marginRight={3}
              >
                <FastField
                  hideNoneOption
                  name="orderNumber"
                  label="Order number"
                  value={values?.orderNumber ?? null}
                  component={SelectAutocompleteField}
                  options={orderingMock}
                  onChange={(value: string) => {
                    setFieldValue?.('orderNumber', value);
                  }}
                />
              </Box>

              {(stepType !== StepTypeEnum.Fork) && (workflowLevel) && (!stepData?.id) && (
                <FormControlLabel
                  name="isDefault"
                  label={<Typography fontWeight="bold">Is Default</Typography>}
                  control={
                    <Checkbox
                      checked={values?.isDefault}
                      onChange={(_, checked) => setFieldValue?.('isDefault', checked)}
                    />
                  }
                />
              )}
            </Stack>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={5}
          mb={6}
        >
          <Grid
            item
            sm={12}
          >
            {(stepType == StepTypeEnum.Step) && (stepData?.id) && (
              <ReasonsGridWrapper>
                <LoaderBackdrop open={loading} />
                <PhoenixGrid
                  canNotCopy
                  withHeader
                  disableSelectionOnClick
                  disableMultipleSelection
                  isFlexible
                  autoRowHeight
                  name={'Reason'}
                  className="reasons-grid"
                  pageSize={5}
                  columns={reasonsColumns}
                  loading={!reasonsData}
                  rows={reasonsData}
                  actions={reasonGridActions}
                  validateSchema={reasonLenderMappingValidationSchema}
                  buttonTitle="Add New Record"
                  btn={AddButton}
                />
              </ReasonsGridWrapper>
            )}
          </Grid>
        </Grid>
        <Typography
          variant="h3"
          mb={2}
          mt={3}
        >
          TRKR Stage Steps Mapping
        </Typography>

        <Grid
          container
          spacing={2}
          mb={2}
        >
          <Grid
            item
            sm={6}
          >
            <SelectAutocomplete
              label="Stages"
              canBeClean={false}
              value={values?.trackerStageId ?? null}
              options={getOptions(trkrStages, 'id', 'title')}
              onChange={(value: string) => {
                setFieldValue?.('trackerStageId', value);
              }}
            />
          </Grid>

          <Grid
            item
            sm={6}
          >
            <SelectAutocomplete
              label="Steps"
              canBeClean={false}
              value={values?.trackerStepId ?? null}
              options={getOptions(trkrSteps, 'id', 'title')}
              onChange={(value: string) => {
                setFieldValue?.('trackerStepId', value);
              }}
            />
          </Grid>
        </Grid>

        {(stepType === StepTypeEnum.ReRouteAll || stepType === StepTypeEnum.ReRoute) && (
          <>
            <Typography
              variant="h3"
              mb={2}
            >
              Destination Step
            </Typography>

            <Grid
              container
              spacing={2}
              mb={2}
            >
              <Grid
                item
                sm={12}
              >
                <FastField
                  disabled
                  name="stepPath"
                  label="Destination step"
                  value={values?.stepPath}
                  component={TextInputField}
                />
              </Grid>

              <Grid
                item
                sm={12}
              >
                <StepList workflows={workflows} handleChangeDestStep={handleChangeDestStep} stepData={stepData} />
              </Grid>
            </Grid>
          </>
        )}

        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h3">Step Role</Typography>

          <FormControlLabel
            name="isBold"
            label={<Typography fontWeight="bold">Is Bold</Typography>}
            control={
              <Checkbox
                checked={values?.isTextBold}
                onChange={(_, checked) => setFieldValue?.('isTextBold', checked)}
              />
            }
          />
        </Stack>

        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            sm={6}
          >
            <SelectAutocomplete
              multiple
              label="All roles"
              value={values?.roleIds ?? null}
              options={getOptions(roles)}
              onChange={(value: string) => {
                setFieldValue?.('roleIds', value);
              }}
            />
          </Grid>

          <Grid
            item
            sm={6}
          >
            <SelectAutocomplete
              label="Comment color"
              value={values?.textCommentColor ?? null}
              options={colorsMock}
              onChange={(value: string) => {
                setFieldValue?.('textCommentColor', value);
              }}
            />
          </Grid>
          <Grid
            item
            sm={6}
          >
            <SelectAutocomplete
              multiple
              label="Can Undo"
              value={values?.roleIdsCanUndo ?? null}
              options={getOptions(roles)}
              onChange={(value: string) => {
                setFieldValue?.('roleIdsCanUndo', value);
              }}
            />
          </Grid>

        </Grid>
      </PageContent>

      <CommonButtonWrapper>
        <CancelButton onClick={handleClose} />
        <SaveButton />
      </CommonButtonWrapper>
    </>
  );
};
